import { Check, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { RAIL_CARD_BAND, RailBottomBar } from "@/components/tuw/post/PriceRail"
import { GUEST, WORDING } from "@/lib/openHouse/wording"
import { cn } from "@/lib/utils"
import { SaveLaterButton } from "./Bookmark"
import { ErrorLine } from "./UploadControl"

// The guest page's price (guest-wording §2): the server's exact quote in the
// rail (a fixed bottom bar and sheet at or below 1080, as today's PriceRail),
// the states that stop posting (unavailable, combination, a saved draft's
// changed price), and the price section on Review. `display` is the quote as
// shown (lib/openHouse/guest.js quoteDisplay), present only for a current quote.

const FROM = "From"

function Spinner({ children }) {
  return (
    <span className="flex items-center gap-2 text-[12.5px] font-semibold text-muted-foreground">
      <Loader2 className="size-4 animate-spin" strokeWidth={2} />
      {children}
    </span>
  )
}

function Block({ children }) {
  return (
    <div role="alert" className="flex flex-col gap-1.5 rounded-xl border border-border bg-accent px-3.5 py-3 text-[12.5px] leading-snug text-muted-foreground">
      {children}
    </div>
  )
}

const Heading = ({ children }) => <p className="font-display text-[13px] font-bold text-foreground">{children}</p>

// change: changeLines() of a changed price still to accept, or null.
export function PriceNotice({ status, change, accepted, accepting, onRetry, onReview, onAccept }) {
  if (status === "unavailable") {
    return (
      <Block>
        <Heading>{GUEST.unavailableHeading}</Heading>
        <p>{GUEST.unavailableBody}</p>
        <div className="mt-1 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={onRetry}>{GUEST.unavailableRetry}</Button>
          <Button size="sm" variant="ghost" onClick={onReview}>{GUEST.review}</Button>
        </div>
      </Block>
    )
  }
  if (status === "combination") {
    return (
      <Block>
        <p>{GUEST.combination}</p>
        <Button size="sm" variant="outline" className="mt-1 self-start" onClick={onReview}>{GUEST.review}</Button>
      </Block>
    )
  }
  if (change) {
    return (
      <Block>
        <Heading>{change.heading}</Heading>
        <p>{GUEST.changedBody}</p>
        <p>{change.previous}</p>
        <p className="font-semibold text-foreground">{change.current}</p>
        <Button size="sm" className="mt-1 self-start" onClick={onAccept} disabled={accepting}>{change.accept}</Button>
      </Block>
    )
  }
  if (!accepted) return null
  return (
    <p role="status" className="flex items-center gap-1.5 text-[12px] font-semibold text-done">
      <Check className="size-3.5 shrink-0" strokeWidth={2.4} />
      {GUEST.changedAccepted}
    </p>
  )
}

function Lines({ display }) {
  if (display.open) return <p className="text-[12px] leading-snug text-muted-foreground">{WORDING.openLevelsTotal}</p>
  return (
    <div>
      {display.lines.map((line, i) => (
        <div key={i} className="flex items-baseline justify-between gap-3 py-1.5 text-[12.5px]">
          <span className="text-muted-foreground">{line.label}</span>
          <span className="whitespace-nowrap font-display font-bold text-foreground">{line.amount}</span>
        </div>
      ))}
      <div className="my-2 h-px bg-border" />
      <div className="flex items-baseline justify-between gap-3 pt-1">
        <span className="text-[13px] font-bold text-foreground">{GUEST.totalLabel}</span>
        <span className="whitespace-nowrap font-display text-[16px] font-bold text-foreground">{display.value}</span>
      </div>
    </div>
  )
}

// price: { status: "idle" | "loading" | "ready" | "unavailable" | "combination" }.
// notice: the rendered PriceNotice (the phone shows it above the step instead).
// onSave: opens the save dialog from the desktop card (the phone's is under Back).
export function GuestPriceRail({ price, display, notice, onSave }) {
  const figure = price.status === "loading" ? (
    <div className="mt-2"><Spinner>{GUEST.loading}</Spinner></div>
  ) : (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-2 font-display text-[24px] font-bold leading-tight tracking-tight text-foreground">
      {display?.from && <span className="text-[13px] font-semibold text-muted-foreground">{FROM}</span>}
      {display?.value ?? "—"}
    </div>
  )

  const card = (inSheet) => (
    <div className={cn("overflow-hidden rounded-2xl border border-border bg-card", inSheet && "rounded-b-none border-b-0")}>
      <div className={cn("border-b border-border bg-accent px-5 py-4", inSheet && "pr-11")}>
        {!inSheet && (
          <>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{GUEST.priceHeading}</div>
            {figure}
          </>
        )}
        <p className={cn("text-[11.5px] leading-snug text-muted-foreground", !inSheet && "mt-2.5")}>
          {display?.open ? GUEST.openTax : GUEST.taxNote}
        </p>
      </div>
      {!inSheet && onSave && (
        <div className={cn("px-5 py-4", (display || notice) && "border-b border-border")}>
          <SaveLaterButton onClick={onSave} className="w-full" />
        </div>
      )}
      {(display || (!inSheet && notice)) && (
        <div className="flex flex-col gap-3 px-5 py-4">
          {!inSheet && notice}
          {display && <Lines display={display} />}
        </div>
      )}
      <p className="border-t border-border bg-accent px-5 py-3 text-[12px] font-semibold leading-snug text-muted-foreground">
        {GUEST.paymentTiming}
      </p>
    </div>
  )

  return (
    <>
      <div data-testid="rail-card" className={RAIL_CARD_BAND}>{card(false)}</div>
      <RailBottomBar
        eyebrow={GUEST.priceHeading}
        from={display?.from ? FROM : null}
        value={price.status === "loading" ? <Loader2 className="size-5 animate-spin" /> : display?.value ?? "—"}
        hint="Free to post"
      >
        {card(true)}
      </RailBottomBar>
    </>
  )
}

// Review's price section; `required` when posting was tried before accepting a changed price.
export function ReviewPrice({ loading, display, notice, required }) {
  return (
    <div data-post-field="price" className="overflow-hidden rounded-xl border border-border">
      <div className="border-b border-border bg-accent px-4 py-2.5">
        <h3 className="font-display text-[13.5px] font-bold tracking-tight text-foreground">{GUEST.priceHeading}</h3>
      </div>
      {display && (
        <dl className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border px-4 py-2.5">
          <dt className="text-[12.5px] font-semibold text-foreground">{display.open ? WORDING.openLevels : GUEST.totalLabel}</dt>
          <dd className="min-w-0 break-words text-right text-[12.5px] text-muted-foreground">
            {display.from && `${FROM} `}{display.value}
          </dd>
        </dl>
      )}
      <div className="flex flex-col gap-2.5 px-4 py-3 text-[12px] leading-snug text-muted-foreground">
        {loading && <Spinner>{GUEST.loading}</Spinner>}
        {display && <p>{display.open ? WORDING.openLevelsTotal : GUEST.fixed}</p>}
        {notice}
        {required && <ErrorLine>{GUEST.changedRequired}</ErrorLine>}
      </div>
    </div>
  )
}
